import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { CertWarning, certReasonLabel } from '../../lib/cert-warnings';
import { formatCertDays } from '../../lib/cert-days';
import { formatCalendarDate } from '../../lib/calendar-date';

/**
 * One renderer for `warnings[]`, used by the confirm dialog AND the toast that
 * follows a forced assign.
 *
 * The 409 and the 200 carry the identical array, so the crew reads the same
 * sentence before the decision and after it. Two renderers would eventually
 * describe a different override than the one that got written to
 * `tenant_audit_log`.
 *
 * The day figure comes from lib/cert-days.ts like every other cert-day counter
 * in the app, so "past working-day limit" and the number beside it cannot
 * disagree with what the equipment screens show for the same unit.
 */
export function CertWarningList({ warnings, compact = false }: { warnings: CertWarning[]; compact?: boolean }) {
  if (warnings.length === 0) return null;

  return (
    <ul className={compact ? 'space-y-1' : 'space-y-3'}>
      {warnings.map((w) => (
        <li
          key={w.equipment_id}
          className={compact ? '' : 'bg-amber-500/10 border border-amber-500/30 rounded-lg p-3'}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white">
                {w.name || w.serial_number || 'Equipment'}
                {w.name && w.serial_number ? (
                  <span className="font-normal text-fieldiq-muted"> · {w.serial_number}</span>
                ) : null}
              </p>
              {!compact && (
                <ul className="mt-1 space-y-0.5">
                  {w.reasons.map((r) => (
                    <li key={r} className="text-xs text-amber-300">
                      {certReasonLabel(r)}
                      {/* The evidence, not just the verdict. "Past working-day
                          limit" with no numbers gives the crew nothing to weigh;
                          "42 of 30 working days" does. */}
                      {r === 'day_limit_exceeded' && (
                        <span className="text-fieldiq-muted">
                          {' '}— {formatCertDays(
                            { daysUsed: w.working_days_since_inspection, dayLimit: w.cert_day_limit },
                            'used',
                          )}
                        </span>
                      )}
                      {r === 'cert_expired' && w.cert_expiry_date && (
                        <span className="text-fieldiq-muted">
                          {' '}— expired {formatCalendarDate(w.cert_expiry_date, {
                            month: 'short', day: 'numeric', year: 'numeric',
                          })}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {compact && (
                <p className="text-xs text-amber-300">{w.reasons.map(certReasonLabel).join(' · ')}</p>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default CertWarningList;
